import React, { useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { AdminPageLayout, AdminSectionCard } from "../../components/admin/AdminPageLayout";
import { AdminAction, AdminFormFooter } from "../../components/admin";
import { API_ORIGIN as API_URL } from "../../config/env";
import { resolveMediaUrl } from "../../utils/mediaUrl";

const emptyForm = {
  title: "",
  description: "",
  category: "scheme",
  state: "",
  district: "",
  eligibility: "",
  benefits: "",
  applicationUrl: "",
  startDate: "",
  endDate: "",
};

const inputClassName =
  "block w-full rounded-xl border-0 py-2.5 text-stone-900 shadow-sm ring-1 ring-inset ring-stone-300 placeholder:text-stone-400 focus:ring-2 focus:ring-inset focus:ring-stone-900 sm:text-sm sm:leading-6 transition-shadow px-4";

function toInputDate(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function cleanPayload(form) {
  return {
    ...form,
    state: form.state.trim() || null,
    district: form.district.trim() || null,
    description: form.description.trim() || null,
    eligibility: form.eligibility.trim() || null,
    benefits: form.benefits.trim() || null,
    applicationUrl: form.applicationUrl.trim() || null,
    startDate: form.startDate || null,
    endDate: form.endDate || null,
  };
}

export default function SchemeFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const token = localStorage.getItem("rfis");
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [file, setFile] = useState(null);
  const [image, setImage] = useState("");

  useEffect(() => {
    if (!isEdit) return;

    async function fetchScheme() {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/api/v1/government-schemes/${id}`, {
          headers: { rf_token_header123_k: token },
        });
        const scheme = res.data.data || {};
        setForm({
          title: scheme.title || "",
          description: scheme.description || "",
          category: scheme.category || "scheme",
          state: scheme.state || "",
          district: scheme.district || "",
          eligibility: scheme.eligibility || "",
          benefits: scheme.benefits || "",
          applicationUrl: scheme.applicationUrl || "",
          startDate: toInputDate(scheme.startDate),
          endDate: toInputDate(scheme.endDate),
        });
        setFile(null);
        setImage(resolveMediaUrl(scheme.imageUrl || scheme.image));
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to load scheme");
        navigate("/government-schemes", { replace: true });
      } finally {
        setLoading(false);
      }
    }

    fetchScheme();
  }, [id, isEdit, navigate, token]);

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const errors = {
    title: submitted && !form.title.trim() ? "Title is required" : "",
    category: submitted && !form.category ? "Category is required" : "",
    endDate:
      submitted && form.startDate && form.endDate && form.endDate < form.startDate
        ? "End date cannot be before start date"
        : "",
  };

  const changeHandler = (event) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    const allowedFormats = ["image/png", "image/webp", "image/jpeg"];
    if (allowedFormats.includes(selected.type)) {
      setFile(selected);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImage(e.target.result);
      };
      reader.readAsDataURL(selected);
    } else {
      event.target.value = "";
      toast.error(
        "Image type should be image/png', 'image/webp', 'image/jpeg ",
        {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: true,
        }
      );
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitted(true);

    const submitErrors = {
      title: !form.title.trim(),
      category: !form.category,
      endDate: form.startDate && form.endDate && form.endDate < form.startDate,
    };

    if (Object.values(submitErrors).some(Boolean)) {
      toast.error("Please fix the highlighted fields");
      return;
    }

    try {
      setSaving(true);
      const payload = cleanPayload(form);
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        formData.append(key, value == null ? "" : String(value));
      });
      if (file instanceof File) {
        formData.append("image", file);
      }

      const headers = { rf_token_header123_k: token };
      if (isEdit) {
        await axios.put(`${API_URL}/api/v1/government-schemes/${id}`, formData, {
          headers,
        });
        toast.success("Scheme updated");
      } else {
        await axios.post(`${API_URL}/api/v1/government-schemes`, formData, {
          headers,
        });
        toast.success("Scheme created");
      }
      navigate("/government-schemes", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save scheme");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminPageLayout
      eyebrow="Schemes & Training"
      title={isEdit ? "Edit Scheme" : "Add New Scheme"}
      description="Create state and district specific schemes or training programs with eligibility, benefits, and active dates."
      actions={
        <AdminAction
          variant="outline"
          startIcon={<ArrowBack fontSize="small" />}
          onClick={() => navigate("/government-schemes")}
        >
          Back to listing
        </AdminAction>
      }
    >
      <AdminSectionCard
        title="Scheme details"
        description="Leave state empty for PAN India, and leave district empty for all districts in the selected state."
      >
        {loading ? (
          <div className="py-16 text-center">
            <CircularProgress size={28} />
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <div className="admin-form-body overflow-y-auto max-h-[70vh]">
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium leading-6 text-stone-700 mb-3">
                    Scheme Image
                  </label>
                  <div
                    className="min-h-[200px] border-2 border-dashed rounded-xl bg-stone-50 flex flex-col items-center justify-center cursor-pointer transition-colors hover:bg-stone-100 hover:border-stone-400"
                    onClick={() => document.getElementById("scheme-image-input").click()}
                  >
                    <input
                      id="scheme-image-input"
                      type="file"
                      accept="image/png, image/webp, image/jpeg"
                      onChange={changeHandler}
                      className="hidden"
                    />
                    {image ? (
                      <div className="w-full h-full flex items-center justify-center p-4">
                        <img
                          src={image}
                          alt="Preview"
                          className="max-h-[180px] object-contain rounded-lg"
                          onError={() => setImage("")}
                        />
                      </div>
                    ) : (
                      <div className="text-center p-6">
                        <svg className="mx-auto h-12 w-12 text-stone-400 mb-3" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-stone-700 font-medium">Click or drag file to this area to upload</p>
                        <p className="text-stone-400 text-sm mt-1">PNG, WEBP, JPEG (max 5MB)</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={form.title}
                      placeholder="Enter scheme title"
                      className={inputClassName}
                      onChange={(e) => updateForm("title", e.target.value)}
                    />
                    {errors.title ? (
                      <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                    ) : null}
                  </div>

                  <div>
                    <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                      Category *
                    </label>
                    <select
                      value={form.category}
                      className={inputClassName}
                      onChange={(e) => updateForm("category", e.target.value)}
                    >
                      <option value="scheme">Scheme</option>
                      <option value="training">Training Program</option>
                    </select>
                    {errors.category ? (
                      <p className="mt-1 text-sm text-red-600">{errors.category}</p>
                    ) : null}
                  </div>

                  <div>
                    <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                      Application URL
                    </label>
                    <input
                      type="text"
                      value={form.applicationUrl}
                      placeholder="https://example.com/apply"
                      className={inputClassName}
                      onChange={(e) => updateForm("applicationUrl", e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                      State
                    </label>
                    <input
                      type="text"
                      value={form.state}
                      placeholder="Leave empty for PAN India"
                      className={inputClassName}
                      onChange={(e) => updateForm("state", e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                      District
                    </label>
                    <input
                      type="text"
                      value={form.district}
                      placeholder="Leave empty for all districts"
                      className={inputClassName}
                      onChange={(e) => updateForm("district", e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={form.startDate}
                      className={inputClassName}
                      onChange={(e) => updateForm("startDate", e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={form.endDate}
                      className={inputClassName}
                      onChange={(e) => updateForm("endDate", e.target.value)}
                    />
                    {errors.endDate ? (
                      <p className="mt-1 text-sm text-red-600">{errors.endDate}</p>
                    ) : null}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                      Description
                    </label>
                    <textarea
                      className={`${inputClassName} min-h-[100px] resize-y`}
                      placeholder="Enter scheme description"
                      value={form.description}
                      rows={4}
                      onChange={(e) => updateForm("description", e.target.value)}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                      Eligibility
                    </label>
                    <textarea
                      className={`${inputClassName} min-h-[100px] resize-y`}
                      placeholder="Enter eligibility details"
                      value={form.eligibility}
                      rows={4}
                      onChange={(e) => updateForm("eligibility", e.target.value)}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                      Benefits
                    </label>
                    <textarea
                      className={`${inputClassName} min-h-[100px] resize-y`}
                      placeholder="Enter benefits"
                      value={form.benefits}
                      rows={4}
                      onChange={(e) => updateForm("benefits", e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <AdminFormFooter>
              <AdminAction type="submit" loading={saving}>
                {isEdit ? "Update Scheme" : "Create Scheme"}
              </AdminAction>
            </AdminFormFooter>
          </form>
        )}
      </AdminSectionCard>
    </AdminPageLayout>
  );
}
